import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Auth from '../../Utils/Auth/auth';

import { capitalized, hyphenEveryFourChars } from '../../Utils/formats';
import { useDeviceContextViewport } from '../../contexts/Device/DeviceContext';

import { Button, Pagination, PaginationMobile, Space, Tag } from '../../ds';
import { Select } from '../DS/Select';
import EmptySearchResults from '../EmptySearchResults/index';
import { TdResponsive, ThResponsive } from '../ResponsiveTable';
import { ResponsiveTable } from '../ResponsiveTable/ResponsiveTable';
import './paymentOrderHistory.scss';
import { ReactComponent as RefreshIconMobile } from './assets/refresh-icon-mobile.svg';
import { ReactComponent as RefreshIcon } from './assets/refresh-icon.svg';
import { ReactComponent as SalesIcon } from './assets/sales-icon.svg';
import { useQueryOrders } from './hooks/useQueryOrders';
import { SearchBar } from '../DS/SearchBar';
import { validateNumerical } from '../../Utils/formValidations';
import { useQueryParams } from '../../Utils/useQueryParams';
import { statusColorTag } from '../../Utils/statusColorTag';
import AnalyticsEvent from '../../services/analytics/AnalyticsEvent';
import {
  notApplicableOrderStaffId,
  REFUND_CREATE_FAIL,
} from '../../Utils/constants';
import MerchantPaymentsService from '../../services/MerchantPaymentsService';
import ErrorModal from '../ErrorModal/ErrorModal';
import { useLoaderContext } from '../../contexts/Loader/LoaderContext';
import ModalCancel from './modal/ModalCancel';
import { useTranslation } from 'react-i18next';
import usePusherChannel from '../Pusher/hooks/usePusherChannel';
import isValidTranslation from '../../Utils/isValidTranslation';
import { useToastContext } from '../../contexts/Toast/ToastContext';

const tableHeaders = {
  paymentId: 'ID de pago',
  storeName: 'Sucursal',
  amount: 'Monto',
  createdAt: 'Fecha y hora',
  kueskiAuthorization: 'Estatus',
  storeAuthorization: 'Confirmación del comercio',
  action: 'Acción',
};

const RefreshButton = ({ onClick = () => null }) => (
  <Button type='link' size='small' onClick={onClick}>
    <div className='filter-icon'>
      <RefreshIcon className='k-ds-m-rigth-sp-03' />
      <span>&nbsp;Actualizar</span>
    </div>
    <RefreshIconMobile className='custom-update-icon' />
  </Button>
);

RefreshButton.propTypes = {
  onClick: PropTypes.func,
};

const EmptyResults = () => {
  return (
    <EmptySearchResults
      header='Sin resultados'
      subHeader='Selecciona una sucursal para consultar las ventas.'
    />
  );
};

const NoSales = () => {
  return (
    <EmptySearchResults
      icon={<SalesIcon />}
      header='Aquí verás tus ventas'
      subHeader='Cuando se registren las ventas en tienda física, podrás consultarlas aquí'
    />
  );
};

EmptyResults.propTypes = {
  role: PropTypes.string,
};

function PaymentOrderHistory() {
  const viewport = useDeviceContextViewport();
  const PaginationComponent = viewport.isMobile ? PaginationMobile : Pagination;
  const user = Auth.userData();
  const { permission_scopes } = Auth.userData();
  if (!user || !user.stores) {
    return '';
  }

  const queryParams = useQueryParams();
  const enabledQuery = () => {
    if (user.stores.length === 1) {
      return true;
    }

    return queryParams.get('item') !== null;
  };

  const { t } = useTranslation();
  const [enableQuery, setEnable] = useState(enabledQuery());
  const queryOrders = useQueryOrders({
    storeId: user.stores.length > 1 ? queryParams.get('item') : '',
    enabled: enableQuery,
  });
  const addToast = useToastContext();
  const [searchState, setSearchState] = useState('');
  const [showCancelOrder, setShowCancelOrder] = useState(false);
  const [selectedOrder, setSelecedOrder] = useState(null);
  const [isOpenalreadyPaidErrorInfoModal, setIsOpenAlreadyPaidErrorInfoModal] =
    useState(false);
  const { setIsLoaderOpen } = useLoaderContext();
  const history = useHistory();

  const goDetails = async (orderId, storeId) => {
    if (user.stores.length > 1) {
      await history.replace(`/orders?item=${storeId}`);
    }
    history.push(`/orders/${orderId}/${storeId}`);
    const contentArea = document.getElementById('content-area');
    contentArea.scrollTop = 0;
  };

  const onStoreChange = storeId => {
    setEnable(true);
    setSearchBarIconModeOpen(false);
    queryOrders.updateFilters({
      storeId: storeId,
      page: 1,
      pageSize: 20,
      search: '',
    });
  };

  const { isMobile } = viewport;
  const storeSelectorColumnsClass = isMobile ? 'one-column' : 'two-columns';
  const ordersData = queryOrders.queryResult.data
    ? queryOrders.queryResult.data.orders
    : [];

  const [searchBarIconModeOpen, setSearchBarIconModeOpen] = useState(false);
  const searchBarWidth = viewport.isMobile ? '100%' : '437px';

  const setValidSearch = value => {
    const valueNoHyphens = value.split('-').join('');
    if (validateNumerical(valueNoHyphens)) {
      let searchValue = hyphenEveryFourChars(valueNoHyphens);
      const lastHyphenIndex = value.lastIndexOf('-');
      const hyphenIsOnEveryFour =
        (lastHyphenIndex - Math.floor((valueNoHyphens.length - 1) / 4)) % 4 ===
        0;

      if (
        lastHyphenIndex !== -1 &&
        lastHyphenIndex === value.length - 1 &&
        hyphenIsOnEveryFour
      ) {
        searchValue = `${searchValue}-`;
      }
      setSearchState(searchValue);
    }
  };

  const clearSearch = () => {
    setSearchState('');
    queryOrders.updateFilters({
      storeId: user.stores.length > 1 ? queryOrders.filters.storeId : '',
      search: '',
      page: 1,
      pageSize: 20,
    });
  };

  const handleOnSearch = search => {
    setEnable(true);
    const searchNoHyphens = search.split('-').join('');
    queryOrders.updateFilters({
      storeId: user.stores.length > 1 ? queryOrders.filters.storeId : '',
      page: 1,
      pageSize: 20,
      search: searchNoHyphens,
    });
  };

  const showNoResultsOrSales = () => {
    if (queryOrders.queryResult.isFetching) {
      return null;
    }

    if (user.stores.length === 1) {
      if (ordersData.length === 0) {
        return <NoSales />;
      }
    }

    if (user.stores.length > 1) {
      if (
        queryOrders.filters.storeId === '' ||
        queryOrders.filters.storeId === null
      ) {
        return <EmptyResults />;
      }
    }

    return null;
  };

  const fetchRequestRefund = async () => {
    setShowCancelOrder(false);
    setIsLoaderOpen(true);

    try {
      const orderResponse = await MerchantPaymentsService().getOrderDetail(
        selectedOrder.order_id,
        selectedOrder.store_id
      );

      await MerchantPaymentsService().requestOrderRefund({
        order_id: selectedOrder.order_id,
        staff_id:
          orderResponse.staff_id !== notApplicableOrderStaffId
            ? orderResponse.staff_id
            : '',
        amount: parseFloat(orderResponse.total),
      });
    } catch (error) {
      const responseMessage = error?.response?.data?.message;
      AnalyticsEvent().errorTrack({
        errorType: 'payment-order-details-request-refund-error',
        errorDescription: error,
      });

      if (responseMessage === REFUND_CREATE_FAIL) {
        setIsOpenAlreadyPaidErrorInfoModal(true);
      } else {
        var message = 'Ocurrió un error, ¿Podrías intentarlo de nuevo?';
        const translationPrefix = 'refund.';
        let responseMessageTranslation = '';

        if (responseMessage) {
          responseMessageTranslation = t(translationPrefix + responseMessage, {
            nsSeparator: false,
          });
        }

        if (
          responseMessageTranslation &&
          isValidTranslation(translationPrefix, responseMessageTranslation)
        ) {
          message = responseMessageTranslation;
        }

        addToast(message, 'error');
        setIsLoaderOpen(false);
      }

      console.log(error);
      setIsLoaderOpen(false);
    } finally {
      queryOrders.queryResult.refetch();
      setIsLoaderOpen(false);
    }
  };

  const readOnlyPermission = () => {
    if (permission_scopes) return permission_scopes[0]?.scopes?.length === 1;
  };

  const handleCloseCancelModal = () => {
    setShowCancelOrder(false);
    setSelecedOrder(null);
  };

  const storeId =
    user.stores.length === 1
      ? user.stores[0].store_id
      : queryOrders.filters.storeId || null;

  const handlerPusherCallbak = useCallback(() => {
    queryOrders.queryResult.refetch();
  }, [storeId]);

  usePusherChannel(
    storeId ? `channel_sales_view_${storeId}` : '',
    'order_status_updated',
    handlerPusherCallbak
  );

  return (
    <div className='k-ds-width-full payment-order-history'>
      <div className='header'>Ventas</div>
      {!viewport.isMobile ? (
        <div className='k-ds-subheading-02'>
          Monitorea y gestiona las ventas en tiempo real
        </div>
      ) : null}
      {user.stores.length > 1 && (
        <div className='store-select-dropdown'>
          <div className={storeSelectorColumnsClass}>
            <Select
              type='text'
              className='store-select'
              label='Sucursal'
              placeholder='Elige una opción'
              required
              name='sucursal'
              options={user.stores.map(store => ({
                text: store.name,
                value: store.store_id,
              }))}
              value={queryOrders.filters.storeId}
              onChange={storeId => onStoreChange(storeId)}
            />
          </div>
        </div>
      )}
      <Space
        direction='horizontal'
        justify='space-between'
        className='action-header'
      >
        <SearchBar
          placeholder='Buscar por ID de pago'
          search={searchState}
          setSearch={setValidSearch}
          onSearch={() => handleOnSearch(searchState)}
          clearSearch={() => clearSearch()}
          width={searchBarWidth}
          iconModeOpen={searchBarIconModeOpen}
          setIconModeOpen={() => {
            setSearchBarIconModeOpen(!searchBarIconModeOpen);
          }}
        />
        <Space className='order-filters'>
          <RefreshButton
            onClick={() => {
              if (
                user.stores.length === 1 ||
                (queryOrders.filters.storeId !== '' &&
                  queryOrders.filters.storeId !== null)
              ) {
                queryOrders.queryResult.refetch();
              }
            }}
          />
        </Space>
      </Space>
      <ResponsiveTable
        type='b2b'
        className='k-ds-width-full payment-order-history-table'
      >
        <thead>
          <tr>
            <ThResponsive devices='desktop tablet mobile'>
              {tableHeaders.createdAt}
            </ThResponsive>
            <ThResponsive devices='desktop tablet mobile'>
              {tableHeaders.storeName}
            </ThResponsive>
            <th>{tableHeaders.paymentId}</th>
            <th>{tableHeaders.kueskiAuthorization}</th>
            <th>{tableHeaders.amount}</th>
            {!readOnlyPermission() && <th>{tableHeaders.action}</th>}
          </tr>
        </thead>
        <tbody>
          {ordersData.map(item => {
            const kueskiStatus = item.kueski_authorization;
            return (
              <React.Fragment key={item.order_id}>
                <tr className='row-hover'>
                  <TdResponsive
                    devices='desktop tablet mobile'
                    dataTitle={tableHeaders.createdAt}
                  >
                    {item.created_at_formatted}
                  </TdResponsive>
                  <TdResponsive
                    devices='desktop tablet mobile'
                    dataTitle={tableHeaders.storeName}
                  >
                    {item.store_name}
                  </TdResponsive>
                  <TdResponsive dataTitle={tableHeaders.paymentId}>
                    {!readOnlyPermission() ? (
                      <button
                        onClick={async () =>
                          await goDetails(item.order_id, item.store_id)
                        }
                        className='link-button'
                      >
                        {hyphenEveryFourChars(item.payment_id || '')}
                      </button>
                    ) : (
                      hyphenEveryFourChars(item.payment_id || '')
                    )}
                  </TdResponsive>
                  <td data-title={tableHeaders.kueskiAuthorization}>
                    <Tag color={statusColorTag[kueskiStatus]}>
                      {capitalized(String(kueskiStatus || '').toLowerCase())}
                    </Tag>
                  </td>
                  <TdResponsive
                    noWrap
                    dataTitle={tableHeaders.amount}
                  >{`$ ${item.total}`}</TdResponsive>
                  {!readOnlyPermission() && (
                    <TdResponsive
                      devices='mobile tablet desktop'
                      dataTitle={tableHeaders.action}
                    >
                      {kueskiStatus === 'AUTORIZADO' ? (
                        <button
                          onClick={async () => {
                            setSelecedOrder(item);
                            setShowCancelOrder(true);
                          }}
                          className='link-button'
                        >
                          Cancelar
                        </button>
                      ) : null}
                    </TdResponsive>
                  )}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </ResponsiveTable>
      {ordersData.length > 0 && (
        <PaginationComponent
          totalPages={queryOrders.queryResult.data.total_pages}
          totalElements={queryOrders.queryResult.data.total_records}
          maxVisibleNumbers={6}
          currentPage={queryOrders.queryResult.data.page}
          elementsPerPage={queryOrders.queryResult.data.page_size}
          changePage={page => {
            queryOrders.changePage(Number(page));
          }}
        />
      )}

      {showNoResultsOrSales()}
      <Space
        className='k-ds-m-top-sp-09 k-ds-heading-01'
        id='last-sync'
        direction='horizontal'
        justify='end'
      >
        Última actualización: {queryOrders.lastSync}
      </Space>
      <ErrorModal
        heading=''
        description={
          <div>
            {
              'Para cancelar esta transacción que ya fue liquidada por el cliente, envía ID de pago, monto y tipo de cancelación (parcial/total) a '
            }
            <a
              href='mailto:cancelacioneskueskipay@kueski.com'
              className='k-ds-text-color-interactive-primary'
            >
              cancelacioneskueskipay@kueski.com
            </a>
            .
          </div>
        }
        defaultOpened={isOpenalreadyPaidErrorInfoModal}
        close={setIsOpenAlreadyPaidErrorInfoModal}
        showClose
      />
      <ModalCancel
        isOpen={showCancelOrder}
        onClose={handleCloseCancelModal}
        onClick={async () => await fetchRequestRefund()}
      />
    </div>
  );
}

export default PaymentOrderHistory;
